/**
 * Bir DOM öğesi için kısa ve kararlı bir CSS seçici üretir (öğe seçici aracı).
 * Öncelik: data-tour → anlamlı id → sınıf/etiket yolu (gerektiğinde :nth-of-type),
 * her adımda belgede TEK öğeyle eşleşene kadar yukarı çıkar.
 */

const STATE_CLASS = /^(active|show|showing|hover|focus|focused|open|collapsed|disabled|selected|visible|hidden|fade|in|current|is-|has-|notif-item-|ftour-|aycrm-)/;
const GENERATED_ID = /^(\d|react-|:r|radix-|headlessui-|mui-|select-|rc_|ember)/;

const cssEscape = (v: string) => (typeof CSS !== 'undefined' && CSS.escape ? CSS.escape(v) : v.replace(/([^\w-])/g, '\\$1'));

const stableClasses = (el: Element): string[] =>
  Array.from(el.classList)
    .filter((c) => c && !STATE_CLASS.test(c) && !/\d{3,}/.test(c))
    .slice(0, 3);

const isUnique = (sel: string) => {
  try {
    return document.querySelectorAll(sel).length === 1;
  } catch {
    return false;
  }
};

const partFor = (el: Element, forceNth: boolean): string => {
  const tag = el.tagName.toLowerCase();
  const classes = stableClasses(el)
    .map((c) => `.${cssEscape(c)}`)
    .join('');
  let part = `${tag}${classes}`;
  if (forceNth && el.parentElement) {
    const siblings = Array.from(el.parentElement.children).filter((s) => s.tagName === el.tagName);
    if (siblings.length > 1) part += `:nth-of-type(${siblings.indexOf(el) + 1})`;
  }
  return part;
};

export const buildSelector = (target: Element): string => {
  // 1) data-tour (kendisi ya da yakın atası)
  let el: Element | null = target;
  for (let depth = 0; el && depth < 4; depth += 1) {
    const dt = el.getAttribute('data-tour');
    if (dt) {
      const sel = `[data-tour="${dt}"]`;
      if (isUnique(sel) || el === target) return sel;
    }
    el = el.parentElement;
  }
  // 2) anlamlı id
  if (target.id && !GENERATED_ID.test(target.id)) {
    const sel = `#${cssEscape(target.id)}`;
    if (isUnique(sel)) return sel;
  }
  // 2b) kararlı nitelikler (başlık, erişilebilirlik etiketi, ad, yer tutucu, bağlantı)
  const tag = target.tagName.toLowerCase();
  for (const attr of ['aria-label', 'title', 'name', 'placeholder', 'href', 'data-kt-menu-trigger', 'data-bs-target']) {
    const v = target.getAttribute(attr);
    if (v && v.length <= 120) {
      const sel = `${tag}[${attr}="${v.replace(/"/g, '\\"')}"]`;
      if (isUnique(sel)) return sel;
    }
  }
  // 3) yol: önce tek başına, sonra atalarla; önce sınıfla, gerekirse nth-of-type
  const chain: string[] = [];
  let cur: Element | null = target;
  while (cur && cur !== document.body && cur !== document.documentElement) {
    for (const forceNth of [false, true]) {
      const part = partFor(cur, forceNth);
      const candidate = [part, ...chain].join(' > ');
      if (isUnique(candidate)) return candidate;
      // atanın id'si varsa oradan başla
      if (cur.parentElement && cur.parentElement.id && !GENERATED_ID.test(cur.parentElement.id)) {
        const withId = `#${cssEscape(cur.parentElement.id)} > ${candidate}`;
        if (isUnique(withId)) return withId;
      }
      if (forceNth) chain.unshift(part);
    }
    cur = cur.parentElement;
  }
  return chain.join(' > ');
};

/** Seçicinin kaç öğeyle eşleştiği (0 = geçersiz/yok) */
export const matchCount = (sel: string): number => {
  try {
    return document.querySelectorAll(sel).length;
  } catch {
    return 0;
  }
};

/** Öğenin kısa açıklaması (araç ipucu için) */
export const describe = (el: Element): string => {
  const text = (el.textContent || '').replace(/\s+/g, ' ').trim().slice(0, 40);
  const tag = el.tagName.toLowerCase();
  return text ? `${tag} · "${text}"` : tag;
};
